import { motion, AnimatePresence } from "framer-motion";
import { DotGrid } from "./DotGrid";

const POINTS_A = [
  { x: 120, y: 100 },
  { x: 170, y: 155 },
  { x: 100, y: 190 },
  { x: 155, y: 240 },
];
const POINTS_B = [
  { x: 420, y: 110 },
  { x: 480, y: 140 },
  { x: 450, y: 200 },
  { x: 510, y: 230 },
];
const ALL_POINTS = [...POINTS_A, ...POINTS_B];

const POLYGON_A =
  "M 70,70 C 60,120 65,200 80,260 L 130,275 C 180,270 210,250 205,210 L 210,140 C 200,80 160,60 120,65 Z";
const POLYGON_B =
  "M 390,80 C 380,110 375,200 385,255 L 440,270 C 500,265 540,250 545,215 L 550,130 C 540,85 510,70 460,72 Z";

export const RESPONSIBILITY_DURATIONS = [2500, 2000, 2000, 2000, 2500, 2500, 2500, 2500];

export function ResponsibilityAnimation({
  currentStep,
  cycleKey,
}: {
  currentStep: number;
  cycleKey: number;
}) {
  const showPoints = currentStep >= 0;
  const showPolygons = currentStep >= 1;
  const showNames = currentStep >= 2;
  const showAttributes = currentStep >= 3;
  const showCrmIntegration = currentStep >= 4;
  const showIncoming = currentStep >= 5;
  const showIdentify = currentStep >= 6;
  const showAssigned = currentStep >= 7;

  return (
    <div className="relative">
      <svg
        viewBox="0 0 620 360"
        className="w-full rounded-xl border border-border/40 bg-white/70"
        style={{ aspectRatio: "620/360" }}
      >
        <DotGrid />

        {/* Polygons */}
        {showPolygons && (
          <>
            <motion.path
              d={POLYGON_A}
              fill="hsl(186 60% 40% / 0.12)"
              stroke="hsl(186 60% 60% / 0.6)"
              strokeWidth={2}
              strokeDasharray="1000"
              initial={{ strokeDashoffset: 1000, fillOpacity: 0 }}
              animate={{
                strokeDashoffset: 0,
                fillOpacity: showIdentify ? [1, 0.3, 1, 0.3, 1] : 1,
              }}
              transition={{
                strokeDashoffset: { duration: 1.5 },
                fillOpacity: showIdentify
                  ? { duration: 1.5, repeat: 0 }
                  : { duration: 1.5 },
              }}
            />
            <motion.path
              d={POLYGON_B}
              fill="hsl(0 80% 70% / 0.10)"
              stroke="hsl(0 80% 70% / 0.45)"
              strokeWidth={2}
              strokeDasharray="1000"
              initial={{ strokeDashoffset: 1000, fillOpacity: 0 }}
              animate={{ strokeDashoffset: 0, fillOpacity: 1 }}
              transition={{ duration: 1.5, delay: 0.3 }}
            />
          </>
        )}

        {/* Points */}
        {showPoints &&
          ALL_POINTS.map((p, i) => (
            <motion.circle
              key={`${cycleKey}-p-${i}`}
              cx={p.x}
              cy={p.y}
              r={6}
              fill="hsl(186 60% 40%)"
              stroke="hsl(186 60% 30%)"
              strokeWidth={1.5}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: i * 0.15 }}
            />
          ))}

        {/* Names */}
        {showNames && (
          <>
            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              <rect x="105" y="275" width="90" height="24" rx="6" fill="hsl(186 60% 40% / 0.9)" />
              <text x="150" y="291" textAnchor="middle" fill="white" fontSize="11" fontFamily="Montserrat, system-ui" fontWeight="600">
                Område A
              </text>
            </motion.g>
            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <rect x="415" y="270" width="90" height="24" rx="6" fill="hsl(0 80% 65% / 0.9)" />
              <text x="460" y="286" textAnchor="middle" fill="white" fontSize="11" fontFamily="Montserrat, system-ui" fontWeight="600">
                Område B
              </text>
            </motion.g>
          </>
        )}

        {/* Arrow from CRM card down to point in Område A – step 6 (curves left) */}
        {showIncoming && (
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <motion.path
              d="M 560,40 C 420,20 240,40 155,235"
              fill="none"
              stroke="hsl(var(--petrol) / 0.5)"
              strokeWidth={1.5}
              strokeDasharray="6 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8 }}
            />
            <motion.polygon
              points="151,232 158,238 150,241"
              fill="hsl(var(--petrol) / 0.5)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
            />
          </motion.g>
        )}

        {/* Arrow from same point back to CRM card – step 7 (curves right/below) */}
        {showIdentify && (
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <motion.path
              d="M 160,245 C 280,310 450,280 565,55"
              fill="none"
              stroke="hsl(150 60% 45% / 0.6)"
              strokeWidth={1.5}
              strokeDasharray="6 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8 }}
            />
            <motion.polygon
              points="563,49 567,57 571,49"
              fill="hsl(150 60% 45% / 0.6)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
            />
          </motion.g>
        )}
      </svg>

      {/* Attribute panel */}
      <AnimatePresence>
        {showAttributes && !showCrmIntegration && (
          <motion.div
            className="absolute right-2 top-4 bg-white/95 border border-border rounded-lg shadow-lg p-3 w-44"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(186 60% 40%)" }} />
              <span className="text-xs font-heading font-semibold text-foreground">Område A</span>
            </div>
            <div className="space-y-1.5 text-[11px] font-body">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ansvarig</span>
                <span className="font-medium text-foreground">Anna S.</span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Postnummer</span>
                <span className="font-medium text-foreground">12 st</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CRM integration card */}
      <AnimatePresence>
        {showCrmIntegration && (
          <motion.div
            className="absolute right-2 top-4 bg-white/95 border border-border rounded-lg shadow-lg p-3 w-48"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded bg-petrol/10 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--petrol))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <span className="text-xs font-heading font-semibold text-foreground">CRM-integration</span>
            </div>
            <div className="space-y-1.5 text-[11px] font-body">
              {showIncoming && (
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span className="text-muted-foreground">Nytt ärende inkommet…</span>
                </motion.div>
              )}
              {showIdentify && (
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                  <span className="text-muted-foreground">Atlas letar ansvarig…</span>
                </motion.div>
              )}
              {showAssigned && (
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                  <span className="text-foreground font-medium">Tilldelat: Anna Svensson ✓</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
